import { createAction } from 'actions/utils'
import { apiAction, promiseApiAction } from 'actions/application'
import { TRUCK } from 'utils/constants'
import { getSearchResults } from 'services/search'
import * as dashBuilderSvc from 'services/dash'
import { plantLocationsCodes } from 'constants/dash'

export const SET_DASH_BUILDER_SEARCH_INPUT = 'SET_DASH_BUILDER_SEARCH_INPUT'
export const setDashBuilderSearchInput = createAction(
  SET_DASH_BUILDER_SEARCH_INPUT,
  ['dashBuilderSearchInput']
)

export const SET_PRODUCTION_SCHEDULE_DATE = 'SET_PRODUCTION_SCHEDULE_DATE'
export const setProductionScheduleDate = createAction(
  SET_PRODUCTION_SCHEDULE_DATE,
  ['selectedScheduleDate']
)
export const SET_SELECTED_LOCATION_DROP_DOWN = 'SET_SELECTED_LOCATION_DROP_DOWN'
export const setSelectedLocationDropDown = createAction(
  SET_SELECTED_LOCATION_DROP_DOWN,
  ['selectedLocation']
)

export const TOGGLE_CUSTOM_LAYOUT_SEARCH = 'TOGGLE_CUSTOM_LAYOUT_SEARCH'
export const toggleWithCustomLayouts = createAction(
  TOGGLE_CUSTOM_LAYOUT_SEARCH,
  ['customLayoutSearch']
)

export const FETCH_PRODUCTION_SCHEDULE = 'FETCH_PRODUCTION_SCHEDULE'
export const PRODUCTION_SCHEDULE_RESPONSE_RECEIVED =
  'PRODUCTION_SCHEDULE_RESPONSE_RECEIVED'
// Raw API failure. Carries the failed requestBody, so a saga can decide whether
// the failure still applies to the page the user is looking at.
export const PRODUCTION_SCHEDULE_REQUEST_FAILED =
  'PRODUCTION_SCHEDULE_REQUEST_FAILED'

// Dispatched only once a failure has been confirmed to belong to the CURRENT
// request. The reducer reacts to this, never to the raw failure, so a late
// failure from an abandoned request cannot blank the page now on screen.
export const PRODUCTION_SCHEDULE_PAGE_FAILED = 'PRODUCTION_SCHEDULE_PAGE_FAILED'
export const productionSchedulePageFailed = createAction(
  PRODUCTION_SCHEDULE_PAGE_FAILED,
  ['requestId']
)

// Bumped once per issued production-schedule request. The id travels with the
// request and comes back on both the success and failure actions, so the result
// watcher can tell "this is the answer to the question the user is asking now"
// from "this is the answer to a question the user already moved on from".
//
// A counter rather than a field-by-field comparison: the query spans location,
// date, pagination index, search string, daysOut, custom-layout flag and filter
// terms, and any field left out of a manual comparison becomes a silent race.
export const NEXT_PRODUCTION_SCHEDULE_REQUEST_ID =
  'NEXT_PRODUCTION_SCHEDULE_REQUEST_ID'
export const nextProductionScheduleRequestId = createAction(
  NEXT_PRODUCTION_SCHEDULE_REQUEST_ID
)
export const UPDATE_PROD_SCHEDULE_FROM_API_RESPONSE =
  'UPDATE_PROD_SCHEDULE_FROM_API_RESPONSE'
export const updateProdScheduleFromApiResponse = createAction(
  UPDATE_PROD_SCHEDULE_FROM_API_RESPONSE,
  ['vehicles', 'total', 'collectionName']
)

/**
 * `failureActionType` is required here: without it a failed page request leaves
 * the previous page's vehicles in the store while the paginator has already
 * advanced, so the table renders the prior page's build sequence numbers under
 * the new page's range (duplicated numbers + a missing range).
 *
 * `id` is required too, for a different reason. `apiCall` builds its in-flight
 * signal as `${successActionType}${id || ''}` and silently drops a call whose
 * signal is already in the async set. Without a per-request `id` every page and
 * every date shares one signal, so changing the date while a request is in
 * flight would drop the new request entirely and leave nothing loading the newly
 * selected date. `requestId` makes each request its own signal.
 *
 * `requestId` is also passed through `args`, which `apiCall` spreads onto both
 * the success and failure actions — that is what lets the result watcher discard
 * answers to superseded queries.
 */
export const fetchSearchProductionScheduleResults = ({
  requestBody = null,
  collectionName,
  requestId,
}) =>
  apiAction({
    asyncFn: dashBuilderSvc.fetchProductionSchedule,
    args: { collectionName, requestBody, requestId },
    requestActionType: FETCH_PRODUCTION_SCHEDULE,
    successActionType: PRODUCTION_SCHEDULE_RESPONSE_RECEIVED,
    failureActionType: PRODUCTION_SCHEDULE_REQUEST_FAILED,
    id: requestId,
  })

export const fetchProductionSchedule = (query) => {
  const { date, location, customLayoutSearch, startIndex, collectionName } =
    query

  const requestBody = {
    location: plantLocationsCodes[location],
    date,
    customLayoutSearch,
    count: 10,
    startIndex,
  }

  return promiseApiAction({
    asyncFn: dashBuilderSvc.fetchProductionSchedule,
    args: { requestBody, collectionName },
    requestActionType: FETCH_PRODUCTION_SCHEDULE,
    successActionType: PRODUCTION_SCHEDULE_RESPONSE_RECEIVED,
  })
}
export const FETCH_PAGINATED_PRODUCTION_SCHEDULE =
  'FETCH_PAGINATED_PRODUCTION_SCHEDULE'
export const PAGINATED_PRODUCTION_SCHEDULE_FROM_API_RECEIVED =
  'PAGINATED_PRODUCTION_SCHEDULE_FROM_API_RECEIVED'
export const UPDATE_PAGINATED_PRODUCTION_SCHEDULE =
  'UPDATE_PAGINATED_PRODUCTION_SCHEDULE'
export const updatePaginatedProductionSchedule = createAction(
  UPDATE_PAGINATED_PRODUCTION_SCHEDULE,
  ['paginatedSchedule']
)
export const fetchPaginatedProductionSchedule = (query) => {
  const {
    date,
    location,
    customLayoutSearch,
    pageData,
    newIndex,
    collectionName,
  } = query

  const requestBody = {
    location: plantLocationsCodes[location],
    date,
    customLayoutSearch,
    count: 10,
    startIndex: newIndex,
  }

  return promiseApiAction({
    asyncFn: dashBuilderSvc.fetchProductionSchedule,
    args: { requestBody, collectionName, pageData },
    requestActionType: FETCH_PAGINATED_PRODUCTION_SCHEDULE,
    successActionType: PAGINATED_PRODUCTION_SCHEDULE_FROM_API_RECEIVED,
  })
}

export const FETCH_OVER_HEAD_DASHBOARD_LAYOUT =
  'FETCH_OVER_HEAD_DASHBOARD_LAYOUT'
export const UPDATE_SELECTED_VEHICLE_DATA = 'UPDATE_SELECTED_VEHICLE_DATA'
export const UPDATE_SELECTED_DASH_TYPE = 'UPDATE_SELECTED_DASH_TYPE'
export const UPDATE_OVER_HEAD_DASH_LAYOUT = 'UPDATE_OVER_HEAD_DASH_LAYOUT'

export const DASH_BLACK_LIST_RECEIVED = 'DASH_BLACK_LIST_RECEIVED'
export const OVER_HEAD_DASH_LAYOUT_RECEIVED = 'OVER_HEAD_DASH_LAYOUT_RECEIVED'

export const VEHICLE_FROM_SEARCH_BAR_RESPONSE_RECEIVED =
  'VEHICLE_FROM_SEARCH_BAR_RESPONSE_RECEIVED'

export const UPDATE_DASH_VIEW = 'UPDATE_DASH_VIEW'

export const SET_SERIAL_NUMBER_SELECT = 'SET_SERIAL_NUMBER_SELECT'
export const setSerialNumberSelect = createAction(SET_SERIAL_NUMBER_SELECT, [
  'id',
])

export const SET_DASH_BUILDER_FOUND_RESULT = 'SET_DASH_BUILDER_FOUND_RESULT'
export const setDashBuilderFoundResult = createAction(
  SET_DASH_BUILDER_FOUND_RESULT,
  ['foundResult']
)

export const SET_DASH_BUILDER_SEARCH_VALUE = 'SET_DASH_BUILDER_SEARCH_VALUE'
export const setDashBuilderSearchValue = createAction(
  SET_DASH_BUILDER_SEARCH_VALUE,
  ['searchValue']
)

export const REQUESTING_DASH_BUILDER_SEARCH = 'REQUESTING_DASH_BUILDER_SEARCH'
export const SUCCESS_DASH_BUILDER_SEARCH = 'SUCCESS_DASH_BUILDER_SEARCH'
export const FAILURE_DASH_BUILDER_SEARCH = 'FAILURE_DASH_BUILDER_SEARCH'
export const fetchSearchResults = ({ search }) => {
  const requestBody = {
    search,
    count: 1,
    startIndex: 0,
  }
  const id = `${TRUCK}-${search}`

  return apiAction({
    asyncFn: getSearchResults,
    args: { collectionName: TRUCK, requestBody },
    requestActionType: REQUESTING_DASH_BUILDER_SEARCH,
    successActionType: SUCCESS_DASH_BUILDER_SEARCH,
    failureActionType: FAILURE_DASH_BUILDER_SEARCH,
    id,
  })
}
